import { useQuery, useMutation, useQueryClient } from 'react-query';
import ApiService from '@/services/api';
import { IModemListParams, IModemCreateRequest, IModemUpdateRequest, IModemAssignRequest } from '@/types/api';

// Query keys
export const MODEM_QUERY_KEYS = {
  all: ['modems'] as const,
  lists: () => [...MODEM_QUERY_KEYS.all, 'list'] as const,
  list: (params: IModemListParams) => [...MODEM_QUERY_KEYS.lists(), params] as const,
  details: () => [...MODEM_QUERY_KEYS.all, 'detail'] as const,
  detail: (modemId: string) => [...MODEM_QUERY_KEYS.details(), modemId] as const,
};

// Hook for fetching modems list
export const useModems = (params: IModemListParams = {}) => {
  return useQuery(
    MODEM_QUERY_KEYS.list(params),
    () => ApiService.getModems(params),
    {
      keepPreviousData: true,
      staleTime: 30000, // 30 seconds
      cacheTime: 300000, // 5 minutes
    }
  );
};

// Hook for fetching single modem
export const useModem = (modemId: string) => {
  return useQuery(
    MODEM_QUERY_KEYS.detail(modemId),
    () => ApiService.getModem(modemId),
    {
      enabled: !!modemId,
      staleTime: 60000, // 1 minute
    }
  );
};

// Hook for creating modem
export const useCreateModem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: IModemCreateRequest) => ApiService.createModem(data),
    {
      onSuccess: () => {
        // Invalidate and refetch modems list
        queryClient.invalidateQueries(MODEM_QUERY_KEYS.lists());
      },
    }
  );
};

// Hook for updating modem
export const useUpdateModem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ modemId, data }: { modemId: string; data: IModemUpdateRequest }) =>
      ApiService.updateModem(modemId, data),
    {
      onSuccess: (_, { modemId }) => {
        // Invalidate and refetch modem data
        queryClient.invalidateQueries(MODEM_QUERY_KEYS.detail(modemId));
        queryClient.invalidateQueries(MODEM_QUERY_KEYS.lists());
      },
    }
  );
};

// Hook for deleting modem
export const useDeleteModem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (modemId: string) => ApiService.deleteModem(modemId),
    {
      onSuccess: () => {
        // Invalidate and refetch modems list
        queryClient.invalidateQueries(MODEM_QUERY_KEYS.lists());
      },
    }
  );
};

// Hook for assigning modem to customer (v2 - sends to customer's gRPC server)
export const useAssignModem = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (data: IModemAssignRequest) => ApiService.assignModem(data),
    {
      onSuccess: () => {
        // Invalidate and refetch modems list after successful assignment
        queryClient.invalidateQueries(MODEM_QUERY_KEYS.lists());
      },
    }
  );
};